/**
 * Shared FHWA / generic-roadway code-to-label mappings consumed by multiple
 * apps (SafeCurves, MIRE, and future roadway apps), plus the small cell-
 * formatter helpers that pair with them.
 *
 * Domain-specific mappings (e.g. SafeCurves' priority-rating labels) stay
 * in the relevant app's `columns.ts`.
 */

import type { CellContent } from '#lib/components/Attributes.types.js';

/**
 * Look `value` up in `map`. Returns:
 *   - `''` when value is null/undefined,
 *   - the mapped label when present,
 *   - the stringified raw value as a fallback.
 *
 * JS coerces object keys to strings, so a `Record<number, string>` works with
 * either numeric or string-form keys at runtime.
 */
function lookup(value: unknown, map: Record<string | number, string>): string {
	if (value === null || value === undefined) return '';
	return map[value as string | number] ?? String(value);
}

/** TanStack-table cell formatter — `cell: lookupCell(map)`. */
export const lookupCell =
	(map: Record<string | number, string>) => (info: { getValue: () => unknown }) =>
		lookup(info.getValue(), map);

/** AttributesLayout cell formatter — `cell: lookupAttrCell(map)`. */
export const lookupAttrCell = (map: Record<string | number, string>) => (info: CellContent) =>
	lookup(info, map);

/** FHWA functional system codes. */
export const mapRoadSystem: Record<number, string> = {
	1: 'Interstate Highway',
	2: 'Principal Arterial - Other Freeways and Expressways',
	3: 'Principal Arterial - Other',
	4: 'Minor Arterial',
	5: 'Major Collector',
	6: 'Minor Collector',
	7: 'Local Road'
};

/** Paved (1) / unpaved (0) surface type. */
export const mapRoadSeal: Record<number, string> = {
	1: 'Paved',
	0: 'Unpaved'
};

/** FHWA ownership codes. */
export const mapOwnership: Record<number, string> = {
	1: 'State Highway Agency',
	2: 'County Highway Agency',
	3: 'Town or Township Highway Agency',
	4: 'City or Municipal Highway Agency',
	11: 'State Park, Forest, or Reservation Agency',
	12: 'Local Park, Forest or Reservation Agency',
	21: 'Other State Agency',
	25: 'Other Local Agency',
	26: 'Private (other than Railroad)',
	27: 'Railroad',
	31: 'State Toll Road',
	32: 'Local Toll Authority',
	40: 'Other Public Instrumentality (i.e., Airport)',
	50: 'Indian Tribe Nation',
	60: 'Other Federal Agency',
	62: 'Bureau of Indian Affairs',
	63: 'Bureau of Fish and Wildlife',
	64: 'U.S. Forest Service',
	66: 'National Park Service',
	67: 'Tennessee Valley Authority',
	68: 'Bureau of Land Management',
	69: 'Bureau of Reclamation',
	70: 'Corps of Engineers',
	72: 'Air Force',
	73: 'Navy/Marines',
	74: 'Army',
	80: 'Other'
};
